import { getString } from "@/common/uistringlabels/ui-string-utils";
import {
  LCR_PICKUP_STEP_LABEL_KEYS,
  LCR_SHIP_STEP_LABEL_KEYS,
} from "@/pages/OrderTracking/Lcr/lcr.constants";
import type { ProgressStep, ProgressStepState } from "@/pages/OrderTracking/order-tracking.types";
import {
  LCR_DELIVERY_METHOD,
  LCR_STATUS,
  LcrOrder,
  LcrStatus,
} from "@/services/orders/lcr-order.types";

type StatusPosition = { currentIndex: number } | { failedIndex: number; failureKey: string };

const FAILURES: Partial<Record<LcrStatus, { failedIndex: number; failureKey: string }>> = {
  [LCR_STATUS.error]: { failedIndex: 1, failureKey: "orderTracking.lcr.progress.failures.error" },
  [LCR_STATUS.cancelled]: {
    failedIndex: 2,
    failureKey: "orderTracking.lcr.progress.failures.cancelled",
  },
  [LCR_STATUS.processingIssue]: {
    failedIndex: 2,
    failureKey: "orderTracking.lcr.progress.failures.processingIssue",
  },
};

const isPickup = (order: LcrOrder): boolean =>
  order.submission.deliveryMethod !== LCR_DELIVERY_METHOD.shipToHome;

/** A pickup order has no Shipped step, so it stays on Processing until it is ready for pickup. */
const positionFor = (order: LcrOrder, stepCount: number): StatusPosition | undefined => {
  const failure = FAILURES[order.lcrStatus];
  if (failure) {
    return failure;
  }
  switch (order.lcrStatus) {
    case LCR_STATUS.accepted:
      return { currentIndex: 0 };
    case LCR_STATUS.processing:
      return { currentIndex: 2 };
    case LCR_STATUS.shipped:
      return { currentIndex: isPickup(order) ? 2 : 3 };
    case LCR_STATUS.pendingPickup:
      return { currentIndex: 3 };
    case LCR_STATUS.delivered:
      return { currentIndex: stepCount };
    default:
      return undefined;
  }
};

const stateFor = (index: number, currentIndex: number): ProgressStepState => {
  if (index < currentIndex) {
    return "complete";
  }
  return index === currentIndex ? "current" : "upcoming";
};

export const lcrProgressFor = (order: LcrOrder): ProgressStep[] | undefined => {
  const labels = (isPickup(order) ? LCR_PICKUP_STEP_LABEL_KEYS : LCR_SHIP_STEP_LABEL_KEYS).map(
    (key) => getString(key)
  );
  const position = positionFor(order, labels.length);

  if (!position) {
    return undefined;
  }
  if ("failedIndex" in position) {
    return [
      ...labels
        .slice(0, position.failedIndex)
        .map((label): ProgressStep => ({ label, state: "complete" })),
      { label: getString(position.failureKey), state: "failed" },
    ];
  }
  return labels.map((label, index) => ({ label, state: stateFor(index, position.currentIndex) }));
};
